import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Loader2, Plus, Send, Users, Eye, FileText, ChevronRight } from 'lucide-react'
import { ConorbitLoader } from '@/components/ui/ConorbitLoader'

const STATUS_COLORS: Record<string, string> = {
  draft:    'bg-muted text-muted-foreground',
  sent:     'bg-[#E1F5EE] text-[#0F6E56]',
  archived: 'bg-muted text-muted-foreground',
}

interface PRForm {
  title: string
  summary: string
  body: string
  language: string
  embargo_until: string
  contact_name: string
  contact_email: string
  contact_phone: string
  tags: string
}

const DEFAULT_FORM: PRForm = {
  title: '', summary: '', body: '', language: 'en',
  embargo_until: '', contact_name: '', contact_email: '',
  contact_phone: '', tags: '',
}

export function PressReleaseView() {
  const { orgId, can } = useOrgPermissions()
  const { toast } = useToast()
  const qc = useQueryClient()

  const [sheetOpen, setSheetOpen] = useState(false)
  const [editPR, setEditPR] = useState<any | null>(null)
  const [form, setForm] = useState<PRForm>(DEFAULT_FORM)
  const [saving, setSaving] = useState(false)

  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [sendingPR, setSendingPR] = useState<any | null>(null)
  const [selectedJournalists, setSelectedJournalists] = useState<Set<string>>(new Set())
  const [jBeatFilter, setJBeatFilter] = useState('')
  const [sending, setSending] = useState(false)

  const [detailOpen, setDetailOpen] = useState(false)
  const [detailPR, setDetailPR] = useState<any | null>(null)

  // Press releases
  const { data: pressReleases, isLoading } = useQuery({
    queryKey: ['press-releases', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('press_releases')
        .select('*, press_release_recipients(count)')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data || []) as any[]
    },
  })

  // Journalists for sending
  const { data: journalists } = useQuery({
    queryKey: ['journalists-for-pr', orgId],
    enabled: !!orgId && sendDialogOpen,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('journalists')
        .select('id, first_name, last_name, email, beats, seniority, status')
        .eq('org_id', orgId)
        .eq('status', 'active')
        .order('last_name')
      return (data || []) as any[]
    },
  })

  // Recipients for detail view
  const { data: recipients } = useQuery({
    queryKey: ['pr-recipients', detailPR?.id],
    enabled: !!detailPR?.id && detailOpen,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('press_release_recipients')
        .select(`
          id, sent_at, opened_at, coverage_url, coverage_submitted_at,
          journalists(first_name, last_name, email, beats)
        `)
        .eq('press_release_id', detailPR.id)
        .order('sent_at')
      return (data || []) as any[]
    },
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        org_id:        orgId,
        title:         form.title.trim(),
        summary:       form.summary.trim() || null,
        body:          form.body.trim(),
        language:      form.language,
        embargo_until: form.embargo_until || null,
        contact_name:  form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        tags:          form.tags.split(',').map(t => t.trim()).filter(Boolean),
      }
      if (editPR) {
        const { error } = await (supabase as any)
          .from('press_releases').update(payload).eq('id', editPR.id)
        if (error) throw error
      } else {
        const { error } = await (supabase as any)
          .from('press_releases').insert(payload)
        if (error) throw error
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['press-releases', orgId] })
      setSheetOpen(false)
      setEditPR(null)
      setForm(DEFAULT_FORM)
      toast({ title: editPR ? 'Press release updated' : 'Press release created' })
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  })

  async function handleSend() {
    if (!sendingPR || selectedJournalists.size === 0) return
    setSending(true)
    try {
      const { data, error } = await supabase.rpc('send_press_release' as any, {
        p_press_release_id: sendingPR.id,
        p_journalist_ids:   Array.from(selectedJournalists),
      })
      if (error) throw error
      const result = data as any
      toast({
        title: `Press release sent to ${result.sent} journalist${result.sent !== 1 ? 's' : ''}`,
        description: result.skipped > 0 ? `${result.skipped} already received it.` : undefined,
      })
      qc.invalidateQueries({ queryKey: ['press-releases', orgId] })
      setSendDialogOpen(false)
      setSendingPR(null)
      setSelectedJournalists(new Set())
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' })
    } finally {
      setSending(false)
    }
  }

  function openCreate() {
    setEditPR(null)
    setForm(DEFAULT_FORM)
    setSheetOpen(true)
  }

  function openEdit(pr: any) {
    setEditPR(pr)
    setForm({
      title:         pr.title,
      summary:       pr.summary ?? '',
      body:          pr.body,
      language:      pr.language,
      embargo_until: pr.embargo_until
        ? new Date(pr.embargo_until).toISOString().slice(0, 16)
        : '',
      contact_name:  pr.contact_name  ?? '',
      contact_email: pr.contact_email ?? '',
      contact_phone: pr.contact_phone ?? '',
      tags:          (pr.tags || []).join(', '),
    })
    setSheetOpen(true)
  }

  const filteredJournalists = (journalists || []).filter((j: any) =>
    !jBeatFilter || (j.beats || []).some((b: string) =>
      b.toLowerCase().includes(jBeatFilter.toLowerCase())
    )
  )

  const selectAll = () =>
    setSelectedJournalists(new Set(filteredJournalists.map((j: any) => j.id)))
  const clearAll = () => setSelectedJournalists(new Set())

  const openCount = (pr: any) => {
    const recips = pr.press_release_recipients
    if (!recips) return null
    return typeof recips === 'number' ? recips : (recips[0]?.count ?? null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Press Releases</h1>
          <p className="text-justify text-muted-foreground text-sm mt-1">
            Write and distribute press releases to your registered journalists.
          </p>
        </div>
        {can('press_releases', 'edit') && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[#0D2240] text-white
                       rounded-lg text-[13px] font-medium hover:bg-[#0a1a30] transition-colors"
          >
            <Plus size={14} /> New Release
          </button>
        )}
      </div>

      {isLoading ? (
        <ConorbitLoader variant="inline" />
      ) : !(pressReleases || []).length ? (
        <div className="text-center py-20 border rounded-xl text-muted-foreground">
          <FileText size={36} className="mx-auto mb-3 opacity-40" />
          <p className="text-justify text-[14px] font-medium">No press releases yet</p>
          <p className="text-justify text-[12.5px] mt-1">
            Create your first press release and distribute it to journalists.
          </p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Title</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Recipients</TableHead>
                <TableHead>Date</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(pressReleases || []).map((pr: any) => (
                <TableRow
                  key={pr.id}
                  className="cursor-pointer hover:bg-muted/20"
                  onClick={() => { setDetailPR(pr); setDetailOpen(true) }}
                >
                  <TableCell>
                    <div className="text-[13px] font-medium">{pr.title}</div>
                    {pr.summary && (
                      <div className="text-[11.5px] text-muted-foreground line-clamp-1 mt-0.5">
                        {pr.summary}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-[12px] text-muted-foreground uppercase">
                    {pr.language}
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-[10.5px] ${STATUS_COLORS[pr.status]}`}>
                      {pr.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[12px] text-muted-foreground">
                    {pr.recipient_count > 0
                      ? <span className="flex items-center gap-1"><Users size={11} />{pr.recipient_count}</span>
                      : ''}
                  </TableCell>
                  <TableCell className="text-[11.5px] text-muted-foreground whitespace-nowrap">
                    {new Date(pr.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      {can('press_releases', 'edit') && pr.status === 'draft' && (
                        <>
                          <button
                            onClick={() => openEdit(pr)}
                            className="text-[11px] text-[#0D2240] font-medium hover:underline"
                          >Edit</button>
                          <button
                            onClick={() => { setSendingPR(pr); setSendDialogOpen(true) }}
                            className="flex items-center gap-1 text-[11px] text-[#006B3C]
                                       font-medium hover:underline"
                          >
                            <Send size={10} /> Send
                          </button>
                        </>
                      )}
                      {pr.status === 'sent' && (
                        <button
                          onClick={() => { setSendingPR(pr); setSendDialogOpen(true) }}
                          className="flex items-center gap-1 text-[11px] text-[#0D2240]
                                     font-medium hover:underline"
                        >
                          <Send size={10} /> Resend
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create / Edit sheet */}
      <Sheet open={sheetOpen} onOpenChange={o => { if (!o) { setSheetOpen(false); setEditPR(null) } }}>
        <SheetContent side="right" className="w-full max-w-xl overflow-y-auto">
          <SheetHeader className="mb-5">
            <SheetTitle>{editPR ? 'Edit Press Release' : 'New Press Release'}</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <Field label="Title *">
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. AWALCO Condemns Attack on Press Freedom in the Sahel"
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Summary (shown in notifications)">
              <input
                value={form.summary}
                onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
                placeholder="One sentence overview…"
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Body *">
              <textarea
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                rows={10}
                placeholder="Full press release text…"
                className={INPUT_CLS + ' resize-none'}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Language">
                <select
                  value={form.language}
                  onChange={e => setForm(f => ({ ...f, language: e.target.value }))}
                  className={INPUT_CLS}
                >
                  <option value="en">English</option>
                  <option value="fr">French</option>
                  <option value="ar">Arabic</option>
                  <option value="pt">Portuguese</option>
                  <option value="sw">Swahili</option>
                  <option value="ha">Hausa</option>
                </select>
              </Field>
              <Field label="Embargo Until">
                <input
                  type="datetime-local"
                  value={form.embargo_until}
                  onChange={e => setForm(f => ({ ...f, embargo_until: e.target.value }))}
                  className={INPUT_CLS}
                />
              </Field>
            </div>
            <Field label="Tags (comma-separated)">
              <input
                value={form.tags}
                onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                placeholder="press freedom, West Africa, legislation"
                className={INPUT_CLS}
              />
            </Field>
            <div className="bg-muted/30 rounded-xl p-3 space-y-3">
              <p className="text-justify text-[11.5px] font-medium text-muted-foreground">Contact (optional)</p>
              <Field label="Name">
                <input
                  value={form.contact_name}
                  onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="Media liaison name"
                  className={INPUT_CLS}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email">
                  <input
                    type="email"
                    value={form.contact_email}
                    onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </Field>
                <Field label="Phone">
                  <input
                    value={form.contact_phone}
                    onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </Field>
              </div>
            </div>
            <button
              disabled={!form.title.trim() || !form.body.trim() || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              className="w-full py-2.5 bg-[#006B3C] text-white rounded-lg text-[13px]
                         font-medium hover:bg-[#005A32] disabled:opacity-50 transition-colors"
            >
              {saveMutation.isPending ? 'Saving…' : editPR ? 'Save Changes' : 'Create Draft'}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Send dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={o => { if (!o) { setSendDialogOpen(false); setSendingPR(null); setSelectedJournalists(new Set()) } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Send Press Release</DialogTitle>
          </DialogHeader>
          <p className="text-justify text-[12.5px] text-muted-foreground px-1">
            <strong>{sendingPR?.title}</strong>
            {' '}select journalists to receive this release.
          </p>

          <div className="flex items-center gap-3 px-1">
            <input
              value={jBeatFilter}
              onChange={e => setJBeatFilter(e.target.value)}
              placeholder="Filter by beat…"
              className="flex-1 px-3 py-2 border border-border rounded-lg text-[12.5px]
                         bg-background focus:outline-none focus:border-[#006B3C]"
            />
            <button onClick={selectAll} className="text-[11.5px] text-[#006B3C] font-medium hover:underline">
              Select all
            </button>
            <button onClick={clearAll} className="text-[11.5px] text-muted-foreground hover:underline">
              Clear
            </button>
          </div>

          <div className="flex-1 overflow-y-auto border rounded-xl">
            <table className="w-full text-[12.5px]">
              <thead className="bg-muted/40 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-3 py-2 text-left font-medium">Beats</th>
                  <th className="px-3 py-2 text-left font-medium">Seniority</th>
                </tr>
              </thead>
              <tbody>
                {filteredJournalists.map((j: any) => (
                  <tr
                    key={j.id}
                    className="border-t cursor-pointer hover:bg-muted/20"
                    onClick={() => {
                      const next = new Set(selectedJournalists)
                      next.has(j.id) ? next.delete(j.id) : next.add(j.id)
                      setSelectedJournalists(next)
                    }}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedJournalists.has(j.id)}
                        readOnly
                        className="accent-[#006B3C]"
                      />
                    </td>
                    <td className="px-3 py-2">
                      {j.first_name} {j.last_name}
                      {j.email && <div className="text-[10.5px] text-muted-foreground">{j.email}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(j.beats || []).slice(0, 3).map((b: string) => (
                          <span key={b} className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
                            {b}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground capitalize">
                      {j.seniority}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filteredJournalists.length && (
              <p className="text-justify text-center py-8 text-[13px] text-muted-foreground">
                No journalists match the filter.
              </p>
            )}
          </div>

          <DialogFooter>
            <span className="text-[12px] text-muted-foreground mr-auto">
              {selectedJournalists.size} selected
            </span>
            <button
              onClick={() => { setSendDialogOpen(false); setSendingPR(null) }}
              className="px-4 py-2 border border-border rounded-lg text-[13px] hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={selectedJournalists.size === 0 || sending}
              onClick={handleSend}
              className="flex items-center gap-2 px-4 py-2 bg-[#006B3C] text-white
                         rounded-lg text-[13px] font-medium hover:bg-[#005A32]
                         disabled:opacity-50 transition-colors"
            >
              {sending
                ? <><Loader2 size={13} className="animate-spin" /> Sending…</>
                : <><Send size={13} /> Send to {selectedJournalists.size}</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / tracking sheet */}
      <Sheet open={detailOpen} onOpenChange={o => { if (!o) { setDetailOpen(false); setDetailPR(null) } }}>
        <SheetContent side="right" className="w-full max-w-lg overflow-y-auto">
          {detailPR && (
            <>
              <SheetHeader className="mb-4">
                <SheetTitle className="text-[15px]">{detailPR.title}</SheetTitle>
              </SheetHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <StatCard label="Sent to" value={detailPR.recipient_count ?? 0} />
                  <StatCard
                    label="Opened"
                    value={(recipients || []).filter((r: any) => r.opened_at).length}
                  />
                  <StatCard
                    label="Coverage"
                    value={(recipients || []).filter((r: any) => r.coverage_url).length}
                  />
                </div>

                {(recipients || []).length > 0 && (
                  <div className="border rounded-xl overflow-hidden">
                    <table className="w-full text-[12px]">
                      <thead className="bg-muted/40">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Journalist</th>
                          <th className="px-3 py-2 text-left font-medium">Opened</th>
                          <th className="px-3 py-2 text-left font-medium">Coverage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(recipients || []).map((r: any) => (
                          <tr key={r.id} className="border-t">
                            <td className="px-3 py-2">
                              {r.journalists?.first_name} {r.journalists?.last_name}
                              {r.journalists?.email && (
                                <div className="text-[10.5px] text-muted-foreground">
                                  {r.journalists.email}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {r.opened_at ? (
                                <span className="text-[#006B3C]">
                                  {new Date(r.opened_at).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-muted-foreground"></span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {r.coverage_url ? (
                                <a
                                  href={r.coverage_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#0D2240] hover:underline flex items-center gap-1"
                                  onClick={e => e.stopPropagation()}
                                >
                                  View <ChevronRight size={11} />
                                </a>
                              ) : (
                                <span className="text-muted-foreground"></span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {detailPR.body && (
                  <div>
                    <p className="text-justify text-[11.5px] font-medium text-muted-foreground mb-2">Full release</p>
                    <div className="text-[12.5px] text-foreground whitespace-pre-wrap bg-muted/20
                                    rounded-xl p-4 border max-h-72 overflow-y-auto">
                      {detailPR.body}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}

const INPUT_CLS = 'w-full border border-border rounded-lg px-3 py-2 text-[12.5px] bg-background focus:outline-none focus:border-[#006B3C]'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-muted-foreground mb-1">{label}</label>
      {children}
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-muted/20 border rounded-xl p-3">
      <div className="text-[22px] font-bold text-foreground">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  )
}
